// src/admin/AdminOrders.js - تحديث الملف لإضافة طريقة عرض بديلة
import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, getDocs, deleteDoc, doc, query, where } from "firebase/firestore";
import AdminSidebar from "../components/AdminSidebar";
import { useGoogleMaps, Marker, EnhancedGoogleMap } from './../useGoogleMaps';
import OrderCard from './OrderCard';
import OrderDetailsModal from './OrderDetailsModal';
import OrderTrackingModal from './OrderTrackingModal';
import './AdminOrders.css';
import '../styles/order-modals.css';
import '../styles/order-card.css';
import { useLanguage } from '../contexts/LanguageContext';

// استيراد الأيقونات
const deliveredIconUrl = '/icons/delivered-icon.svg';
const pickupIconUrl = '/icons/pickup-icon.svg';
const pendingIconUrl = '/icons/pending-icon.svg';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const { isLoaded } = useGoogleMaps();
  const [filterStatus, setFilterStatus] = useState('all');
  const [restaurants, setRestaurants] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState('all');
  const [selectedDriver, setSelectedDriver] = useState('all');
  const [selectedOrdersForDeletion, setSelectedOrdersForDeletion] = useState([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showTrackingModal, setShowTrackingModal] = useState(false);
  const [orderToTrack, setOrderToTrack] = useState(null);
  const { t } = useLanguage();

  useEffect(() => {
    // جلب جميع الطلبات من قاعدة البيانات
    const fetchOrders = async () => {
      try {
        setIsLoading(true);
        console.log("📌 جاري جلب الطلبات...");
        const querySnapshot = await getDocs(collection(db, "orders"));
        const data = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        console.log("📌 البيانات المسترجعة:", data);
        setOrders(data);
        
        // استخراج قائمة المطاعم الفريدة من الطلبات
        const uniqueRestaurants = [...new Set(data.map(order => order.restaurantId))]
          .filter(id => id) // إزالة القيم الفارغة
          .map(id => {
            const restaurant = data.find(order => order.restaurantId === id);
            return {
              id: id,
              name: restaurant?.restaurantName || 'مطعم غير معروف'
            };
          });
        setRestaurants(uniqueRestaurants);
        
        // استخراج قائمة السائقين الفريدة من الطلبات
        const uniqueDrivers = [...new Set(data.map(order => order.driverId))]
          .filter(id => id) // إزالة القيم الفارغة
          .map(id => {
            const driver = data.find(order => order.driverId === id);
            return {
              id: id,
              name: driver?.driverName || 'سائق غير معروف'
            };
          });
        setDrivers(uniqueDrivers);
      } catch (error) {
        console.error("❌ خطأ في جلب الطلبات:", error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrders();
  }, []);

  // فتح نافذة تفاصيل الطلب
  const handleShowDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  // فتح نافذة تتبع الطلب
  const handleShowTracking = (order) => {
    setOrderToTrack(order.id);
    setShowTrackingModal(true);
  };

  // التعامل مع طلب حذف أحد الطلبات
  const handleDeleteOrder = (order) => {
    setSelectedOrdersForDeletion([order.id]);
    setShowDeleteConfirmation(true);
  };

  // وظيفة حذف الطلبات المحددة
  const deleteSelectedOrders = async () => {
    if (selectedOrdersForDeletion.length === 0) return;
    
    try {
      setIsLoading(true);
      
      // حذف كل طلب محدد من قاعدة البيانات
      for (const orderId of selectedOrdersForDeletion) {
        await deleteDoc(doc(db, "orders", orderId));
        console.log(`✅ تم حذف الطلب: ${orderId}`);
      }
      
      // تحديث قائمة الطلبات المعروضة
      setOrders(orders.filter(order => !selectedOrdersForDeletion.includes(order.id)));
      
      // إعادة تعيين المتغيرات
      setSelectedOrdersForDeletion([]);
      setShowDeleteConfirmation(false);
      
      alert(`تم حذف ${selectedOrdersForDeletion.length} طلب بنجاح`);
    } catch (error) {
      console.error("❌ خطأ في حذف الطلبات:", error.message);
      alert(`حدث خطأ أثناء حذف الطلبات: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // التعامل مع تحديد/إلغاء تحديد كل الطلبات
  const handleSelectAllOrders = (event) => {
    if (event.target.checked) {
      const allOrderIds = getFilteredOrders().map(order => order.id);
      setSelectedOrdersForDeletion(allOrderIds);
    } else {
      setSelectedOrdersForDeletion([]);
    }
  };

  // التعامل مع تحديد/إلغاء تحديد طلب واحد
  const handleSelectOrder = (orderId) => {
    setSelectedOrdersForDeletion(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  };

  // البحث عن طلبات بناءً على معايير متعددة
  const applySearch = async () => {
    try {
      setIsLoading(true);
      
      let orderQuery = collection(db, "orders");
      let constraints = [];
      
      // إضافة قيود البحث إذا كانت محددة
      if (selectedRestaurant !== 'all') {
        constraints.push(where('restaurantId', '==', selectedRestaurant));
      }
      
      if (selectedDriver !== 'all') {
        constraints.push(where('driverId', '==', selectedDriver));
      }
      
      if (filterStatus !== 'all') {
        constraints.push(where('status', '==', filterStatus));
      }
      
      // إذا كانت هناك قيود، قم بإنشاء استعلام مناسب
      let querySnapshot;
      if (constraints.length > 0) {
        const q = query(orderQuery, ...constraints);
        querySnapshot = await getDocs(q);
      } else {
        querySnapshot = await getDocs(orderQuery);
      }
      
      let data = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // تطبيق البحث النصي إذا كان موجوداً
      if (searchTerm) {
        data = data.filter(order => 
          order.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.orderCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          order.phone?.includes(searchTerm)
        );
      }
      
      setOrders(data);
    } catch (error) {
      console.error("❌ خطأ في البحث عن الطلبات:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // تحديد أيقونة الطلب على الخريطة بناءً على حالته
  const getOrderIcon = (order) => {
    if (order.status === "تم التسليم ✅") {
      return deliveredIconUrl;
    } else if (order.status === "تم الاستلام 🚗") {
      return pickupIconUrl;
    } else {
      return pendingIconUrl;
    }
  };

  // تنسيق عرض تفاصيل الطلب المحدد
  const getOrderStatusClass = (status) => {
    if (status?.includes("تم التسليم")) {
      return "status-delivered";
    } else if (status?.includes("تم الاستلام")) {
      return "status-pickup";
    } else if (status?.includes("ملغي")) {
      return "status-cancelled";
    } else {
      return "status-pending";
    }
  };

  // Helper to translate status
  const getOrderStatusLabel = (status) => {
    switch (status) {
      case 'completed':
      case 'تم التسليم ✅':
        return t('orders_delivered');
      case 'pending':
      case 'قيد التنفيذ':
        return t('orders_pending');
      case 'on_way':
      case 'تم الاستلام 🚗':
        return t('orders_on_way');
      case 'cancelled':
      case 'ملغي':
        return t('orders_cancelled');
      default:
        return status || t('unknown');
    }
  };

  // دالة لتصفية الطلبات حسب المعايير المحددة
  const getFilteredOrders = () => {
    return orders.filter(order => {
      // تصفية حسب الحالة
      if (filterStatus !== 'all' && order.status !== filterStatus) {
        return false;
      }
      
      // تصفية حسب المطعم
      if (selectedRestaurant !== 'all' && order.restaurantId !== selectedRestaurant) {
        return false;
      }
      
      // تصفية حسب السائق
      if (selectedDriver !== 'all' && order.driverId !== selectedDriver) {
        return false;
      }
      
      // تصفية حسب البحث النصي
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          (order.customerName?.toLowerCase().includes(searchLower)) ||
          (order.orderCode?.toLowerCase().includes(searchLower)) ||
          (order.id.toLowerCase().includes(searchLower)) ||
          (order.phone?.includes(searchTerm))
        );
      }
      
      return true;
    });
  };

  // تبديل طريقة العرض بين الجدول والبطاقات
  const toggleViewMode = (mode) => {
    setViewMode(mode);
  };

  // عرض بطاقات الطلبات
  const renderOrderCards = () => {
    return (
      <div className="order-cards-grid">
        {getFilteredOrders().map(order => (
          <OrderCard 
            key={order.id}
            order={order}
            onDetails={handleShowDetails}
            onTrack={handleShowTracking}
            onDelete={handleDeleteOrder}
            isSelected={selectedOrdersForDeletion.includes(order.id)}
            onSelect={handleSelectOrder}
          />
        ))}
      </div>
    );
  };

  // عرض جدول الطلبات
  const renderOrdersTable = () => {
    return (
      <div className="orders-table-container">
        <table className="orders-table">
          <thead>
            <tr>
              <th>
                <input 
                  type="checkbox" 
                  onChange={handleSelectAllOrders}
                  checked={selectedOrdersForDeletion.length === getFilteredOrders().length && getFilteredOrders().length > 0}
                />
              </th>
              <th>{t('order_number')}</th>
              <th>{t('restaurant')}</th>
              <th>{t('customer')}</th>
              <th>{t('driver')}</th>
              <th>{t('amount')}</th>
              <th>{t('status')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {getFilteredOrders().map(order => (
              <tr 
                key={order.id} 
                className={selectedOrdersForDeletion.includes(order.id) ? 'selected-row' : ''}
              >
                <td>
                  <input 
                    type="checkbox" 
                    checked={selectedOrdersForDeletion.includes(order.id)}
                    onChange={() => handleSelectOrder(order.id)}
                  />
                </td>
                <td>{order.orderCode || order.id.substring(0, 8)}</td>
                <td>{order.restaurantName || 'غير معروف'}</td>
                <td>
                  <div>
                    <div>{order.customerName || 'غير معروف'}</div>
                    <div className="phone-number">{order.phone || 'لا يوجد رقم'}</div>
                  </div>
                </td>
                <td>{order.driverName || 'غير معروف'}</td>
                <td>{order.amount?.toFixed(3) || '0.000'} د.ك</td>
                <td>
                  <span className={`status-badge ${getOrderStatusClass(order.status)}`}>
                    {getOrderStatusLabel(order.status)}
                  </span>
                </td>
                <td>
                  <div className="actions-cell">
                    <button 
                      className="action-button details" 
                      onClick={() => handleShowDetails(order)}
                    >
                      {t('details')}
                    </button>
                    <button 
                      className="action-button track" 
                      onClick={() => handleShowTracking(order)}
                    >
                      {t('track')}
                    </button>
                    <button 
                      className="action-button delete"
                      onClick={() => handleDeleteOrder(order)}
                    >
                      {t('delete')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <div className="admin-container">
          <h1 className="admin-title">{t('orders_management')}</h1>

          {isLoading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>جاري تحميل البيانات...</p>
            </div>
          ) : (
            <>
          <div className="tabs">
            <button 
              className={`tab ${activeTab === 'list' ? 'active' : ''}`} 
              onClick={() => setActiveTab('list')}
            >
              {t('orders_list')}
            </button>
            <button 
              className={`tab ${activeTab === 'map' ? 'active' : ''}`} 
              onClick={() => setActiveTab('map')}
            >
              {t('orders_map')}
            </button>
          </div>

          {/* فلاتر البحث */}
          <div className="order-filters">
            <div className="filter-container">
              <div className="filter-group">
                <label>{t('restaurant')}:</label>
                <select 
                  value={selectedRestaurant} 
                  onChange={e => setSelectedRestaurant(e.target.value)}
                  className="filter-dropdown"
                >
                  <option value="all">{t('all_restaurants')}</option>
                  {restaurants.map(restaurant => (
                    <option key={restaurant.id} value={restaurant.id}>
                      {restaurant.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label>{t('driver')}:</label>
                <select 
                  value={selectedDriver} 
                  onChange={e => setSelectedDriver(e.target.value)}
                  className="filter-dropdown"
                >
                  <option value="all">{t('all_drivers')}</option>
                  {drivers.map(driver => (
                    <option key={driver.id} value={driver.id}>
                      {driver.name}
                    </option>
                  ))}
                </select>
              </div>
              
              <div className="filter-group">
                <label>{t('status')}:</label>
                <select 
                  value={filterStatus} 
                  onChange={e => setFilterStatus(e.target.value)}
                  className="filter-dropdown"
                >
                  <option value="all">{t('all_statuses')}</option>
                  <option value="completed">{t('orders_delivered')}</option>
                  <option value="on_way">{t('orders_on_way')}</option>
                  <option value="pending">{t('orders_pending')}</option>
                  <option value="cancelled">{t('orders_cancelled')}</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>{t('search')}:</label>
                <input 
                  type="text"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  placeholder={t('order_search_placeholder')}
                  className="search-input"
                />
              </div>
              
              <button className="search-button" onClick={applySearch}>
                {t('search')}
              </button>
            </div>
          </div>

          {/* أزرار الإجراءات */}
          {selectedOrdersForDeletion.length > 0 && (
            <div className="bulk-actions">
              <button 
                className="delete-button" 
                onClick={() => setShowDeleteConfirmation(true)}
              >
                {t('delete_selected_orders')} ({selectedOrdersForDeletion.length})
              </button>
            </div>
          )}

          {/* نافذة تأكيد الحذف */}
          {showDeleteConfirmation && (
            <div className="delete-confirmation">
              <div className="confirmation-dialog">
                <h3>{t('delete_confirmation')}</h3>
                <p>{t('confirm_delete_message')}</p>
                <p className="warning">{t('permanent_delete_warning')}</p>
                <div className="confirmation-actions">
                  <button 
                    className="cancel-button" 
                    onClick={() => setShowDeleteConfirmation(false)}
                  >
                    {t('cancel')}
                  </button>
                  <button 
                    className="confirm-delete-button" 
                    onClick={deleteSelectedOrders}
                  >
                    {t('confirm_delete')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'list' && (
            <div className="orders-content">
              <div className="order-header">
                <div className="order-stats">
                  <div className="stat">
                    <span className="stat-value">{orders.length}</span>
                    <span className="stat-label">{t('total_orders')}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{orders.filter(order => order.status === 'completed').length}</span>
                    <span className="stat-label">{t('orders_delivered')}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{orders.filter(order => order.status === 'on_way').length}</span>
                    <span className="stat-label">{t('orders_on_way')}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{orders.filter(order => order.status === 'pending').length}</span>
                    <span className="stat-label">{t('orders_pending')}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{orders.filter(order => order.status === 'cancelled').length}</span>
                    <span className="stat-label">{t('orders_cancelled')}</span>
                  </div>
                </div>
                
                {/* أزرار تبديل طريقة العرض */}
                <div className="view-toggle">
                  <button 
                    className={`view-toggle-button ${viewMode === 'table' ? 'active' : ''}`} 
                    onClick={() => toggleViewMode('table')}
                  >
                    <i className="fas fa-table"></i>
                    {t('table')}
                  </button>
                  <button 
                    className={`view-toggle-button ${viewMode === 'cards' ? 'active' : ''}`} 
                    onClick={() => toggleViewMode('cards')}
                  >
                    <i className="fas fa-th-large"></i>
                    {t('cards')}
                  </button>
                </div>
              </div>

              {getFilteredOrders().length === 0 ? (
                <div className="no-orders">{t('no_orders_found')}</div>
              ) : (
                viewMode === 'table' ? renderOrdersTable() : renderOrderCards()
              )}
            </div>
          )}

          {activeTab === 'map' && (
            <div className="map-and-details">
              <div className="map-container">
                <div className="map-legend">
                  <div className="legend-item">
                    <div className="legend-color green"></div>
                    <span>{t('delivered')}</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color blue"></div>
                    <span>{t('picked_up')}</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color orange"></div>
                    <span>{t('pending')}</span>
                  </div>
                </div>
                {isLoaded ? (
                  <EnhancedGoogleMap 
                    center={{ lat: 29.3759, lng: 47.9774 }} 
                    zoom={10}
                    mapStyles={{
                      width: '100%',
                      height: '500px',
                      borderRadius: '12px'
                    }}
                  >
                    {getFilteredOrders().map((order) => (
                      <Marker
                        key={order.id}
                        position={order.driverLocation || order.customerLocation || { lat: 29.3759, lng: 47.9774 }}
                        icon={{
                          url: getOrderIcon(order),
                          scaledSize: { width: 40, height: 40 },
                          anchor: { x: 20, y: 40 }
                        }}
                        onClick={() => handleShowDetails(order)}
                      />
                    ))}
                  </EnhancedGoogleMap>
                ) : (
                  <div className="map-loading">
                    <p>{t('loading_map')}</p>
                  </div>
                )}
              </div>

              {selectedOrder && (
                <div className="order-details-card">
                  <div className="card-header">
                    <h2>{t('order_details')}</h2>
                    <button 
                      className="close-button"
                      onClick={() => setSelectedOrder(null)}
                    >
                      &times;
                    </button>
                  </div>
                  <div className="card-body">
                    <div className="order-id">
                      {t('order_number')}: <span>{selectedOrder.orderCode || selectedOrder.id.substring(0, 8)}</span>
                    </div>
                    <div className={`order-status ${getOrderStatusClass(selectedOrder.status)}`}>
                      {getOrderStatusLabel(selectedOrder.status)}
                    </div>
                    <div className="order-info-section">
                      <h3>{t('customer_info')}</h3>
                      <div className="info-row">
                        <span className="info-label">{t('name')}:</span>
                        <span className="info-value">{selectedOrder.customerName}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">{t('phone')}:</span>
                        <span className="info-value">{selectedOrder.phone}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">{t('address')}:</span>
                        <span className="info-value">{selectedOrder.address}</span>
                      </div>
                    </div>
                    <div className="order-info-section">
                      <h3>{t('restaurant_and_delivery')}</h3>
                      <div className="info-row">
                        <span className="info-label">{t('restaurant')}:</span>
                        <span className="info-value">{selectedOrder.restaurantName || t('unknown_restaurant')}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">{t('driver')}:</span>
                        <span className="info-value">{selectedOrder.driverName || t('no_driver_assigned')}</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">{t('amount')}:</span>
                        <span className="info-value">{selectedOrder.amount?.toFixed(3) || t('unknown_amount')} د.ك</span>
                      </div>
                      <div className="info-row">
                        <span className="info-label">{t('delivery_fee')}:</span>
                        <span className="info-value">{selectedOrder.deliveryFee?.toFixed(3) || t('unknown_delivery_fee')} د.ك</span>
                      </div>
                    </div>
                    <div className="order-actions">
                      <button 
                        className="action-button track"
                        onClick={() => handleShowTracking(selectedOrder)}
                      >
                        {t('track_driver')}
                      </button>
                      <button 
                        className="action-button delete"
                        onClick={() => handleDeleteOrder(selectedOrder)}
                      >
                        {t('delete_order')}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* نافذة تفاصيل الطلب */}
          {showDetailsModal && selectedOrder && (
            <OrderDetailsModal 
              order={selectedOrder} 
              onClose={() => setShowDetailsModal(false)} 
            />
          )}

          {/* نافذة تتبع الطلب */}
          {showTrackingModal && orderToTrack && (
            <OrderTrackingModal 
              orderId={orderToTrack} 
              onClose={() => {
                setShowTrackingModal(false);
                setOrderToTrack(null);
              }} 
            />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminOrders;